import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import '~/components/home/home.scss';
import ChartBudgetRequestsComponent from '../budget-requests/chart-budget-requests/chart-budget-requests.vue';
import { Data, User } from '~/utils';
import FilesBudgetRequestsComponent from '../budget-requests/files-budget-requests/files-budget-requests.vue';
import { MasterService } from '~/services/master-service';
import { MtpPeriod, MtpStatus } from '~/utils/interfaces';
import { MtpService } from '~/services/mtp-service';
import { convertBrowserDateToServerDate } from '~/utils/date-helper';
import { CrService } from '~/services/cr-service';
import { MiniFdPeriod } from '~/models/services/cr-period';

type ChartSeries = {
    title: string;
    series: number[];
};

enum OverviewDataType {
    BUDGET_REQUEST = 1,
    CHANGE_REQUEST = 2,
}

@Component({
    components: {
        'chart-budget-requests': ChartBudgetRequestsComponent,
        'files-budget-request': FilesBudgetRequestsComponent
    }
})
export default class OverviewComponent extends Vue {
    private readonly STATUS_CONFIG = {
        [OverviewDataType.BUDGET_REQUEST]: [
            {
                status: MtpStatus.DRAFT,
                color: '#d6d6d6',
                label: 'Draft',
            },
            {
                status: MtpStatus.SUBMITTED,
                color: '#607d8b',
                label: 'Submitted',
            },
            {
                status: MtpStatus.YELLOW_FLAGGED,
                color: '#ffab00',
                label: 'Yellow Flagged',
            },
            {
                status: MtpStatus.REVIEWED,
                color: '#19c142',
                label: 'Reviewed',
            },
            {
                status: MtpStatus.APPROVED,
                color: '#0078dc',
                label: 'Approved',
            },
            {
                status: MtpStatus.RED_FLAGGED,
                color: '#fc0d1b',
                label: 'Red Flagged',
            },
        ],
        [OverviewDataType.CHANGE_REQUEST]: [
            {
                status: 1,
                color: '#949597',
                label: 'Draft',
            },
            {
                status: 3,
                color: '#9ec15b',
                label: 'In Review',
            },
            {
                status: 4,
                color: '#3eaaa5',
                label: 'Approved',
            },
            {
                status: 5,
                color: '#f7bf70',
                label: 'Rejected',
            },
        ],
    } as const;

    private readonly OVERVIEW_DATA_TYPE_CONFIG = {
        [OverviewDataType.BUDGET_REQUEST]: {
            faucetKey: 'mtpStatus',
        },
        [OverviewDataType.CHANGE_REQUEST]: {
            faucetKey: 'miniFdStatus',
        },
    } as const;

    private currentOverviewType = OverviewDataType.BUDGET_REQUEST;
    private userPermissions = <any>[];
    private countries = <any>[];
    private selectedCountry = null;
    private loading: boolean = false;
    private data: Data = Data.Instance;
    private MtpService: MtpService = new MtpService();
    private CrService: CrService = new CrService();
    private currentYear = new Date(Date.now()).getFullYear();
    private mtpPeriod: MtpPeriod | null = null;
    private isMtpPeriod: boolean = false;
    private isSelectedYearInActivePeriod: boolean = false;
    private mtpYear = Data.Instance.currentMtpPeriod?.year ?? this.currentYear;
    private miniFdYear = 0;
    private selectedMtpFilter = `${this.mtpYear}-0`;
    private years = null;
    private isAccessDeniedModalActive: boolean = false;

    private chartSeries: ChartSeries[] = [];

    private get chartOptions() {
        return {
            colors: this.STATUS_CONFIG[this.currentOverviewType].map(e => e.color),
            labels: this.STATUS_CONFIG[this.currentOverviewType].map(e => e.label),
            states: {
                normal: {
                    filter: {
                        type: 'none',
                        value: 0,
                    }
                },
                hover: {
                    filter: {
                        type: 'none',
                        value: 0.15,
                    }
                },
                active: {
                    allowMultipleDataPointsSelection: false,
                    filter: {
                        type: 'none',
                        value: 0.35
                    }
                }
            },
            dataLabels:
                {
                    enabled: false,
                },
            plotOptions: {
                pie: {
                    expandOnClick: false,
                    donut: {
                        labels: {
                            show: true,
                            name: {
                                show: true,
                            },
                            value: {
                                show: true,
                            },
                            total: {
                                show: true
                            }
                        }
                    }
                }
            },
            legend: {
                formatter: function (seriesName, opts) {
                    return [opts.w.globals.series[opts.seriesIndex] + ' - ' + seriesName];
                },
                fontSize: '16px',
                offsetX: -10,
                offsetY: 10

            },
            stroke: {
                show: true,
                curve: 'smooth',
                width: 0,
            },
        };
    }

    async created() {
        this.userPermissions = await User.getPerm();
        await this.checkMtpPeriod();
        await this.updateSelectedYearCurrentPeriodFlag();
    }

    private async updateSelectedYearCurrentPeriodFlag() {
        this.isSelectedYearInActivePeriod = false;

        if (this.miniFdYear === 0) {
            // MTP entry selected in dropdown.
            this.isSelectedYearInActivePeriod = !!this.mtpPeriod && this.isMtpPeriod && this.mtpPeriod.year === this.mtpYear;
            return;
        }

        // CR entry selected in dropdown (miniFdYear !== 0): check active CR periods against selected year.
        try {
            const crPeriods: MiniFdPeriod[] = await this.CrService.getCrPeriods();
            const currentDate = Date.now();

            this.isSelectedYearInActivePeriod = crPeriods.some(crPeriod => {
                if (!crPeriod?.startDate || !crPeriod?.endDate) {
                    return false;
                }

                const crStartDate = new Date(crPeriod.startDate).getTime();
                const crEndDate = new Date(crPeriod.endDate).getTime();
                const isCurrentlyActive = crStartDate <= currentDate && crEndDate >= currentDate;

                const startYear = new Date(crPeriod.startDate).getFullYear();
                const endYear = new Date(crPeriod.endDate).getFullYear();
                const matchesSelectedYear = Number(crPeriod.year) === this.mtpYear || (startYear <= this.mtpYear && endYear >= this.mtpYear);

                return isCurrentlyActive && matchesSelectedYear;
            });
        } catch (error: any) {
            this.isSelectedYearInActivePeriod = false;
        }
    }

    private async checkMtpPeriod() {
        this.isMtpPeriod = false;

        if (localStorage.mtpPeriod) {
            this.mtpPeriod = JSON.parse(localStorage.mtpPeriod);
        }

        // Always refresh from API so updated period dates are respected; cached value is fallback only.
        try {
            this.mtpPeriod = await this.MtpService.GetMtpPeriod(this.currentYear);
            localStorage.setItem('mtpPeriod', JSON.stringify(this.mtpPeriod));
        } catch (error: any) {
            if (error.response !== undefined && error.response.status === 400) {
                localStorage.removeItem('mtpPeriod');
                this.mtpPeriod = null;
            }
        }

        if (this.mtpPeriod && this.mtpPeriod.mtpEndDate && this.mtpPeriod.mtpStartDate) {
            const currentDate = new Date(convertBrowserDateToServerDate(Date.now())).getTime();
            const mtpStartDate = new Date(convertBrowserDateToServerDate(this.mtpPeriod.mtpStartDate)).getTime();
            const mtpEndDate = new Date(convertBrowserDateToServerDate(this.mtpPeriod.mtpEndDate)).getTime();

            if (mtpStartDate <= currentDate && mtpEndDate >= currentDate) {
                this.isMtpPeriod = true;
            }
        }
    }

    async mounted() {
        this.loading = true;
        await this.getCountries();
        await this.fetchChartData();
        if (this.data.constants) {
            this.years = this.data.constants.mtpFilters;
            this.years = this.years.filter(function (e) {
                return e.year !== 0;
            });
            this.years.sort(function (a, b) {
                return b.year - a.year;
            });
        }
        this.loading = false;

        if (!this.userPermissions.includes('OverviewPageView')) {
            $('#accessDeniedModal').modal('show');
            this.isAccessDeniedModalActive = true;
        }

    }

    beforeRouteLeave(to, from, next) {
        if (this.isAccessDeniedModalActive) {
            next(false);
        } else {
            next(true);
        }
    }

    private async getCountries() {
        this.countries = await MasterService.instance.chartService.getCountries();
    }

    private async changeMtpYear() {
        this.loading = true;

        this.mtpYear = parseInt(this.selectedMtpFilter.split('-')[0]);
        this.miniFdYear = parseInt(this.selectedMtpFilter.split('-')[1]);
        await this.updateSelectedYearCurrentPeriodFlag();
        await this.fetchChartData();

        this.loading = false;
    }

    private async fetchChartData() {
        const chartData = await MasterService.instance.chartService.getOverviewBudgetDistribution(this.selectedCountry, this.mtpYear, this.miniFdYear);

        this.chartSeries = [];
        this.currentOverviewType = chartData.isChangeRequestInstance ? OverviewDataType.CHANGE_REQUEST : OverviewDataType.BUDGET_REQUEST;

        const globalSeries: number[] = [];
        this.STATUS_CONFIG[this.currentOverviewType].forEach(e => {
            globalSeries.push(chartData.globalMtpStatusDistributionResponse.budgetRequests.find(item => item[this.OVERVIEW_DATA_TYPE_CONFIG[this.currentOverviewType].faucetKey] === e.status)?.counter ?? 0);
        });

        this.chartSeries.push({
            title: 'Global',
            series: globalSeries,
        });

        chartData.technologyMtpStatusDistributionResponses.forEach(technology => {
            const currentSeries: number[] = [];

            this.STATUS_CONFIG[this.currentOverviewType].forEach(e => {
                currentSeries.push(technology.budgetRequests.find(item => item[this.OVERVIEW_DATA_TYPE_CONFIG[this.currentOverviewType].faucetKey] === e.status)?.counter ?? 0);
            });

            this.chartSeries.push({
                title: technology.technologyName,
                series: currentSeries,
            });
        });
    }

    private async changeCountry() {
        this.loading = true;
        await this.fetchChartData();
        this.loading = false;
    }

    private onClickCancel(value) {
        this.isAccessDeniedModalActive = value;
    }
}
